import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Grid,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  CircularProgress,
  InputAdornment,
  Divider,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Switch,
  FormControlLabel,
  TablePagination,
} from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import ListIcon from "@mui/icons-material/List";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import DescriptionIcon from "@mui/icons-material/Description";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import CloseIcon from "@mui/icons-material/Close";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import { useQuery, useQueries, useMutation, useQueryClient } from "react-query";
import client from "../../../../API";
import { StyledTableRow, StyledTableCell } from "../../../../Utils/styledcell";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import useAlertDialog from "../../../../Components/useAlertDialog";
import AlertDialog from "../../../../Components/AlertDialog";
import CompanyDialog from "../info";
import {
  StyleButtonContainter,
  StyleContentWithNavBar,
} from "../../../../Utils/ThemedStyles";

const CompanyDetailsView = () => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [isPayCreatorDialogOpen, setPayCreatorDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [searchTerm, setSearchTerm] = useState("");
  const [spendingSort, setSpendingSort] = useState("");
  const [vendorFilter, setVendorFilter] = useState("all");
  const [blitzpayFilter, setBlitzpayFilter] = useState("all");
  const [collabsFilter, setCollabsFilter] = useState("all");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [spendingPowerDialogOpen, setSpendingPowerDialogOpen] = useState(false);
  const [msaDialogOpen, setMsaDialogOpen] = useState(false);
  const [starterLineDialogOpen, setStarterLineDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [paginationEnabled, setPaginationEnabled] = useState(false);
  const { dialogState, openDialog } = useAlertDialog();
  const queryClient = useQueryClient();

  const { data: companies = [], isLoading } = useQuery(
    ["companies"],
    () => client.companies.list({ limit: 10000, offset: 0 }), // Always fetch all companies
    { staleTime: 10 * 60 * 1000 } // 10 minutes
  );

  /* ---------- computations ---------- */

  const companiesArray = Array.isArray(companies)
    ? companies
    : companies?.companies || [];

  const totalCompaniesFromAPI = companies?.total || companiesArray.length;

  const companiesWithSpendingPower = useMemo(
    () =>
      companiesArray.filter(
        (c) => c.spending_power_limit != null && c.spending_power_limit !== "N/A"
      ),
    [companiesArray]
  );

  // Fetch invoice summaries for all companies in a single request
  const { data: invoiceSummaries } = useQuery(
    ["companiesInvoiceSummaries"],
    () => client.companies.getInvoiceSummaries(),
    {
      staleTime: 30 * 60 * 1000, // 30 minutes
      enabled: companiesWithSpendingPower.length > 0,
    }
  );

  // Map invoice summaries to companies
  const pendingInvoicesByCompany = useMemo(() => {
    if (!invoiceSummaries?.summaries) return {};

    const map = {};
    invoiceSummaries.summaries.forEach((summary) => {
      map[summary.company_id] = summary.pending_amount;
    });
    return map;
  }, [invoiceSummaries]);

  // BlitzPay-specific stats
  const blitzpayStats = useMemo(() => {
    const blitzpayCompanies = companiesArray.filter((c) => c.blitzpay_account);

    const companiesWithMSA = blitzpayCompanies.filter(
      (c) => c.msa_initiated?.initiated
    );

    const companiesWithStarterLine = blitzpayCompanies.filter(
      (c) => c.starter_line_initiated?.initiated
    );

    const totalBlitzpaySpending = blitzpayCompanies.reduce(
      (sum, c) => sum + parseFloat(c.spending_power_limit || 0),
      0
    );

    const companiesWithSpending = blitzpayCompanies.filter(
      (c) =>
        c.spending_power_limit &&
        parseFloat(c.spending_power_limit) > 0
    );

    return {
      total: blitzpayCompanies.length,
      companiesWithMSA,
      companiesWithStarterLine,
      totalBlitzpaySpending,
      companiesWithSpending,
    };
  }, [companiesArray]);

  // Collabs stats (for quick count / potential future UI)
  const collabsStats = useMemo(() => {
    const collabCompanies = companiesArray.filter((c) => c.collabs_access);
    return {
      total: collabCompanies.length,
      companies: collabCompanies,
    };
  }, [companiesArray]);

  const totalCompanies = totalCompaniesFromAPI;

  const formatAmount = (amt) =>
    amt == null
      ? "N/A"
      : `$${parseFloat(amt)
          .toFixed(2)
          .replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;

  // Apply client-side filtering
  let displayedCompanies = [...companiesArray];

  // Filter by search term
  if (searchTerm) {
    const lowerSearch = searchTerm.toLowerCase();
    displayedCompanies = displayedCompanies.filter(
      (c) =>
        c.name?.toLowerCase().includes(lowerSearch) ||
        c.id?.toString().includes(lowerSearch) ||
        c.sub_account?.toLowerCase().includes(lowerSearch)
    );
  }

  // Filter by BlitzPay status
  if (blitzpayFilter !== "all") {
    displayedCompanies = displayedCompanies.filter((c) =>
      blitzpayFilter === "true" ? c.blitzpay_account : !c.blitzpay_account
    );
  }

  // Filter by Collabs access
  if (collabsFilter !== "all") {
    displayedCompanies = displayedCompanies.filter((c) =>
      collabsFilter === "true" ? c.collabs_access : !c.collabs_access
    );
  }

  // Filter by vendor system
  if (vendorFilter !== "all") {
    displayedCompanies = displayedCompanies.filter((c) =>
      vendorFilter === "true" ? c.vendor_system : !c.vendor_system
    );
  }

  // Priority & spending power sorting:
  // 1) BlitzPay access
  // 2) Collabs access
  // 3) Spending power (if selected)
  const sortByPriorityAndSpending = (a, b) => {
    const aPriority =
      (a.blitzpay_account ? 2 : 0) + (a.collabs_access ? 1 : 0);
    const bPriority =
      (b.blitzpay_account ? 2 : 0) + (b.collabs_access ? 1 : 0);

    if (aPriority !== bPriority) {
      // Higher priority first
      return bPriority - aPriority;
    }

    if (!spendingSort) return 0;

    const aSpending = parseFloat(a.spending_power_limit || 0);
    const bSpending = parseFloat(b.spending_power_limit || 0);

    if (spendingSort === "desc") return bSpending - aSpending;
    if (spendingSort === "asc") return aSpending - bSpending;

    return 0;
  };

  displayedCompanies.sort(sortByPriorityAndSpending);

  // Store the total filtered count before slicing (for pagination)
  const filteredCompaniesCount = displayedCompanies.length;

  // Apply pagination slicing only when pagination is enabled
  if (paginationEnabled) {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    displayedCompanies = displayedCompanies.slice(startIndex, endIndex);
  }

  const { mutate: deleteCompanies, isLoading: isDeleting } = useMutation(
    client.companies.delete,
    {
      onSuccess: () => {
        setSelectedIds([]);
        queryClient.invalidateQueries(["companies"]);
        openDialog("Success", "Companies deleted successfully");
      },
      onError: (e) =>
        openDialog("Error", e?.response?.data?.error || e.message),
    }
  );

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPage(0); // Reset to first page when searching
  };

  const handleBlitzpayFilterChange = (value) => {
    setBlitzpayFilter(value);
    setPage(0);
  };

  const handleCollabsFilterChange = (value) => {
    setCollabsFilter(value);
    setPage(0);
  };

  const handleVendorFilterChange = (value) => {
    setVendorFilter(value);
    setPage(0);
  };

  const handleSelectItem = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const handleSelectAll = () => {
    if (selectedIds.length === displayedCompanies.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(displayedCompanies.map((c) => c.id));
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleShareLink = (name, companyId) => {
    const url = `https://www.useblitz.co/register/${name}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(companyId);
      setTimeout(() => setCopiedId(null), 2000);
      window.open(url, "_blank");
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "pending":
        return "warning";
      case "inactive":
        return "error";
      default:
        return "default";
    }
  };

  const handleOpenCompanyFromDialog = (company, closeDialog) => {
    closeDialog();
    setSelectedCompany(company);
    setDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          ...StyleContentWithNavBar,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ ...StyleContentWithNavBar, pb: 4 }}>
      <AlertDialog alertState={dialogState} />

      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Company Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your companies, track spending power lines, and monitor BlitzPay
          accounts and Collabs access
        </Typography>
      </Box>

      {/* Summary Cards Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Total Companies */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    width: 48,
                    height: 48,
                  }}
                >
                  <BusinessIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {totalCompanies}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Companies
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* BlitzPay Accounts + Spending Power */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              background: "linear-gradient(135deg, #ff416c 0%, #ff4b2b 90%)",
              color: "white",
              height: "100%",
              cursor:
                blitzpayStats.companiesWithSpending.length > 0
                  ? "pointer"
                  : "default",
              transition: "all 0.2s",
              "&:hover":
                blitzpayStats.companiesWithSpending.length > 0
                  ? {
                      boxShadow: 4,
                      transform: "translateY(-2px)",
                    }
                  : {},
            }}
            onClick={() => {
              if (blitzpayStats.companiesWithSpending.length > 0) {
                setSpendingPowerDialogOpen(true);
              }
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    width: 48,
                    height: 48,
                  }}
                >
                  <FlashOnIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {blitzpayStats.total}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    BlitzPay Accounts
                  </Typography>
                  {blitzpayStats.totalBlitzpaySpending > 0 && (
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.8, mt: 0.5, display: "block" }}
                    >
                      {formatAmount(blitzpayStats.totalBlitzpaySpending)} spending
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* MSA Initiated */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              bgcolor: "primary.50",
              height: "100%",
              cursor:
                blitzpayStats.companiesWithMSA.length > 0
                  ? "pointer"
                  : "default",
              transition: "all 0.2s",
              "&:hover":
                blitzpayStats.companiesWithMSA.length > 0
                  ? {
                      boxShadow: 2,
                      transform: "translateY(-2px)",
                    }
                  : {},
            }}
            onClick={() => {
              if (blitzpayStats.companiesWithMSA.length > 0) {
                setMsaDialogOpen(true);
              }
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar
                  sx={{
                    bgcolor: "primary.100",
                    width: 48,
                    height: 48,
                  }}
                >
                  <DescriptionIcon color="primary" />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {blitzpayStats.companiesWithMSA.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    MSA Initiated
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Starter Lines Active */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              bgcolor: "success.50",
              height: "100%",
              cursor:
                blitzpayStats.companiesWithStarterLine.length > 0
                  ? "pointer"
                  : "default",
              transition: "all 0.2s",
              "&:hover":
                blitzpayStats.companiesWithStarterLine.length > 0
                  ? {
                      boxShadow: 2,
                      transform: "translateY(-2px)",
                    }
                  : {},
            }}
            onClick={() => {
              if (blitzpayStats.companiesWithStarterLine.length > 0) {
                setStarterLineDialogOpen(true);
              }
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar
                  sx={{
                    bgcolor: "success.100",
                    width: 48,
                    height: 48,
                  }}
                >
                  <TrendingFlatIcon color="success" />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {blitzpayStats.companiesWithStarterLine.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Starter Lines Active
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Bar */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Stack spacing={3}>
          {/* Primary Actions */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedIds([]);
                setPayCreatorDialogOpen(true);
              }}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
                "&:hover": {
                  boxShadow: "0 6px 16px rgba(102, 126, 234, 0.5)",
                },
              }}
            >
              Add Company
            </Button>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              disabled={selectedIds.length !== 1}
              onClick={() => setPayCreatorDialogOpen(true)}
            >
              Edit Company
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={
                isDeleting ? <CircularProgress size={16} /> : <DeleteOutlineIcon />
              }
              disabled={!selectedIds.length || isDeleting}
              onClick={() => deleteCompanies({ companyIds: selectedIds })}
            >
              Delete ({selectedIds.length})
            </Button>

            {selectedIds.length > 0 && (
              <Chip
                label={`${selectedIds.length} selected`}
                onDelete={() => setSelectedIds([])}
                color="primary"
                sx={{ ml: "auto" }}
              />
            )}
          </Stack>

          <Divider />

          {/* Search Bar - Now in its own prominent row */}
          <TextField
            variant="outlined"
            size="medium"
            placeholder="Search companies by name, ID, or sub-account..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
              }
            }}
          />

          {/* Filters and View Controls */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            flexWrap="wrap"
          >
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>BlitzPay Status</InputLabel>
              <Select
                label="BlitzPay Status"
                value={blitzpayFilter}
                onChange={(e) => handleBlitzpayFilterChange(e.target.value)}
              >
                <MenuItem value="all">All Companies</MenuItem>
                <MenuItem value="true">BlitzPay Only</MenuItem>
                <MenuItem value="false">Non-BlitzPay Only</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Collabs Access</InputLabel>
              <Select
                label="Collabs Access"
                value={collabsFilter}
                onChange={(e) => handleCollabsFilterChange(e.target.value)}
              >
                <MenuItem value="all">All Companies</MenuItem>
                <MenuItem value="true">Collabs Enabled</MenuItem>
                <MenuItem value="false">No Collabs</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Vendor System</InputLabel>
              <Select
                label="Vendor System"
                value={vendorFilter}
                onChange={(e) => handleVendorFilterChange(e.target.value)}
              >
                <MenuItem value="all">All Companies</MenuItem>
                <MenuItem value="true">Vendor System</MenuItem>
                <MenuItem value="false">Non-Vendor</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Sort by Spending Power</InputLabel>
              <Select
                label="Sort by Spending Power"
                value={spendingSort}
                onChange={(e) => setSpendingSort(e.target.value)}
              >
                <MenuItem value="">Default (priority)</MenuItem>
                <MenuItem value="desc">Highest First</MenuItem>
                <MenuItem value="asc">Lowest First</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={paginationEnabled}
                  onChange={(e) => {
                    setPaginationEnabled(e.target.checked);
                    setPage(0); // Reset to first page when toggling
                  }}
                  color="primary"
                />
              }
              label="Pagination"
            />

            <Box sx={{ ml: "auto" }}>
              <Tabs
                value={viewMode}
                onChange={(e, v) => setViewMode(v)}
                textColor="primary"
                indicatorColor="primary"
              >
                <Tab
                  icon={<GridViewIcon />}
                  iconPosition="start"
                  label="Grid"
                  value="grid"
                />
                <Tab
                  icon={<ListIcon />}
                  iconPosition="start"
                  label="List"
                  value="list"
                />
              </Tabs>
            </Box>
          </Stack>

          {/* Small inline Collabs summary under filters */}
          {collabsStats.total > 0 && (
            <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                icon={<GroupWorkIcon />}
                label={`${collabsStats.total} with Collabs Access`}
                size="small"
                color="success"
                variant="outlined"
              />
            </Box>
          )}
        </Stack>
      </Paper>

      {/* Empty State */}
      {displayedCompanies.length === 0 && !isLoading && (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: 2,
            backgroundColor: "grey.50",
          }}
        >
          <BusinessIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No companies found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm ||
            vendorFilter !== "all" ||
            blitzpayFilter !== "all" ||
            collabsFilter !== "all"
              ? "Try adjusting your filters"
              : "Get started by adding your first company"}
          </Typography>
          {!searchTerm &&
            vendorFilter === "all" &&
            blitzpayFilter === "all" &&
            collabsFilter === "all" && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setPayCreatorDialogOpen(true)}
              >
                Add Company
              </Button>
            )}
        </Paper>
      )}

      {/* Company Grid View */}
      {viewMode === "grid" && displayedCompanies.length > 0 && (
        <>
          <Grid container spacing={3}>
            {displayedCompanies.map((company) => {
            const pendingAmt = pendingInvoicesByCompany[company.id] || 0;
            const spendingPowerUsed = parseFloat(company.spending_power_used || 0) + pendingAmt;
            const spendingPowerLimit = parseFloat(company.spending_power_limit || 0);
            const spendingPowerAvailable = spendingPowerLimit - spendingPowerUsed;
            const isSelected = selectedIds.includes(company.id);
            const isBlitzPay = company.blitzpay_account;
            const hasCollabs = company.collabs_access;

            return (
              <Grid item xs={12} sm={6} md={4} xl={3} key={company.id}>
                <Card
                  elevation={isSelected ? 8 : 1}
                  sx={{
                    height: "100%",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    border: isSelected
                      ? "2px solid #667eea"
                      : isBlitzPay
                      ? "2px solid rgba(255, 65, 108, 0.3)"
                      : hasCollabs
                      ? "2px solid rgba(76, 175, 80, 0.3)"
                      : "2px solid transparent",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                    ...(isBlitzPay && {
                      background:
                        "linear-gradient(135deg, rgba(255, 65, 108, 0.05) 0%, rgba(255, 75, 43, 0.05) 100%)",
                    }),
                    ...(!isBlitzPay &&
                      hasCollabs && {
                        background:
                          "linear-gradient(135deg, rgba(76,175,80,0.05) 0%, rgba(129,199,132,0.05) 100%)",
                      }),
                  }}
                  onClick={() => {
                    setSelectedCompany(company);
                    setDialogOpen(true);
                  }}
                >
                  <CardHeader
                    avatar={
                      <Badge
                        overlap="circular"
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right",
                        }}
                        badgeContent={
                          <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{
                              bgcolor: "background.paper",
                              borderRadius: 999,
                              px: 0.5,
                              py: 0.25,
                              boxShadow: 1,
                            }}
                          >
                            {company.vendor_system && (
                              <Tooltip title="Vendor System">
                                <CheckCircleIcon
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    color: "success.main",
                                  }}
                                />
                              </Tooltip>
                            )}
                            {hasCollabs && (
                              <Tooltip title="Collabs Access">
                                <GroupWorkIcon
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    color: "success.main",
                                  }}
                                />
                              </Tooltip>
                            )}
                            {isBlitzPay && (
                              <Tooltip title="BlitzPay Account">
                                <FlashOnIcon
                                  sx={{
                                    width: 18,
                                    height: 18,
                                    color: "#ff416c",
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        }
                      >
                        <Avatar
                          src={company.phphref || ""}
                          alt={company.name}
                          sx={{
                            bgcolor: isBlitzPay
                              ? "#ff416c"
                              : hasCollabs
                              ? "#4caf50"
                              : "#667eea",
                            width: 56,
                            height: 56,
                            fontSize: 24,
                            fontWeight: 600,
                          }}
                        >
                          {company.name?.[0]?.toUpperCase() || "C"}
                        </Avatar>
                      </Badge>
                    }
                    title={
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {company.name}
                        </Typography>
                        {isBlitzPay && company.sub_account && (
                          <Typography variant="caption" color="text.secondary">
                            Sub: {company.sub_account}
                          </Typography>
                        )}
                      </Box>
                    }
                    subheader={
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{ mt: 0.5, flexWrap: "wrap" }}
                      >
                        <Chip
                          label={company.account_status || "Unknown"}
                          size="small"
                          color={getStatusColor(company.account_status)}
                        />
                        {isBlitzPay && (
                          <Chip
                            icon={<FlashOnIcon />}
                            label="BlitzPay"
                            size="small"
                            sx={{
                              background:
                                "linear-gradient(135deg, #ff416c 0%, #ff4b2b 90%)",
                              color: "white",
                            }}
                          />
                        )}
                        {hasCollabs && (
                          <Chip
                            icon={<GroupWorkIcon />}
                            label="Collabs"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    }
                    action={
                      <Checkbox
                        color="primary"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelectItem(company.id);
                        }}
                      />
                    }
                  />
                  <CardContent>
                    <Stack spacing={2}>

                      <Divider />

                      <Stack spacing={1}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Spending Power Used
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatAmount(spendingPowerUsed)}
                          </Typography>
                        </Box>
                        {/* BlitzPay-specific info */}
                        {isBlitzPay && company.spending_power_limit && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Nominal Spending Power Limit
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "#ff416c" }}
                            >
                              {formatAmount(spendingPowerLimit)}
                            </Typography>
                          </Box>
                        )}

                        <Divider />
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600 }}
                            color="success.main"
                          >
                            Spending Power Available
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 700 }}
                            color="success.main"
                          >
                            {formatAmount(spendingPowerAvailable)}
                          </Typography>
                        </Box>
                        {spendingPowerUsed > spendingPowerLimit && spendingPowerLimit > 0 && (
                          <Box sx={{ pt: 1 }}>
                            <Chip
                              label="⚠️ Over Limit"
                              size="small"
                              color="error"
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                        )}
                      </Stack>

                      {/* BlitzPay Status Indicators */}
                      {isBlitzPay && (
                        <Box sx={{ pt: 1 }}>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {company.msa_initiated?.initiated && (
                              <Chip
                                icon={<DescriptionIcon />}
                                label={`MSA: ${
                                  company.msa_initiated.initiated_by || "Yes"
                                }`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            )}
                            {company.starter_line_initiated?.initiated && (
                              <Chip
                                icon={<TrendingFlatIcon />}
                                label="Starter Line"
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        </Box>
                      )}

                      <Box sx={{ pt: 1 }}>
                        <Tooltip
                          title={
                            copiedId === company.id
                              ? "Link copied!"
                              : "Copy onboarding link"
                          }
                        >
                          <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            startIcon={<ContentCopyIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShareLink(company.name, company.id);
                            }}
                            color={copiedId === company.id ? "success" : "primary"}
                          >
                            {copiedId === company.id ? "Copied!" : "Share Link"}
                          </Button>
                        </Tooltip>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        {paginationEnabled && (
          <TablePagination
            component="div"
            count={filteredCompaniesCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
            sx={{ borderTop: 1, borderColor: 'divider', mt: 2 }}
          />
        )}
        </>
      )}

      {/* Company List View */}
      {viewMode === "list" && displayedCompanies.length > 0 && (
        <>
          <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden" }}>
            <TableContainer>
              <Table sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.50" }}>
                  <StyledTableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={
                        selectedIds.length > 0 &&
                        selectedIds.length < displayedCompanies.length
                      }
                      checked={
                        displayedCompanies.length > 0 &&
                        selectedIds.length === displayedCompanies.length
                      }
                      onChange={handleSelectAll}
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Company
                    </Typography>
                  </StyledTableCell>
                
                  <StyledTableCell align="right">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Spending Power Used
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Nominal SP Limit
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Available
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Status
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      BlitzPay
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Collabs
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Actions
                    </Typography>
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedCompanies.map((company) => {
                  const pendingAmt = pendingInvoicesByCompany[company.id] || 0;
                  const spendingPowerUsed = parseFloat(company.spending_power_used || 0) + pendingAmt;
                  const spendingPowerLimit = parseFloat(company.spending_power_limit || 0);
                  const spendingPowerAvailable = spendingPowerLimit - spendingPowerUsed;
                  const isSelected = selectedIds.includes(company.id);
                  const isBlitzPay = company.blitzpay_account;
                  const hasCollabs = company.collabs_access;

                  return (
                    <StyledTableRow
                      key={company.id}
                      hover
                      sx={{
                        backgroundColor: isSelected
                          ? "rgba(102, 126, 234, 0.08)"
                          : isBlitzPay
                          ? "rgba(255, 65, 108, 0.03)"
                          : hasCollabs
                          ? "rgba(76,175,80,0.03)"
                          : undefined,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setSelectedCompany(company);
                        setDialogOpen(true);
                      }}
                    >
                      <StyledTableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectItem(company.id);
                          }}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Avatar
                            src={company.phphref || ""}
                            alt={company.name}
                            sx={{
                              bgcolor: isBlitzPay
                                ? "#ff416c"
                                : hasCollabs
                                ? "#4caf50"
                                : "#667eea",
                              width: 40,
                              height: 40,
                            }}
                          >
                            {company.name?.[0]?.toUpperCase() || "C"}
                          </Avatar>
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                              >
                                {company.name}
                              </Typography>
                              {isBlitzPay && (
                                <FlashOnIcon
                                  sx={{ width: 16, height: 16, color: "#ff416c" }}
                                />
                              )}
                              {hasCollabs && (
                                <GroupWorkIcon
                                  sx={{ width: 16, height: 16, color: "#4caf50" }}
                                />
                              )}
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ID: {company.id}
                              {isBlitzPay &&
                                company.sub_account &&
                                ` • Sub: ${company.sub_account}`}
                            </Typography>
                          </Box>
                          {company.vendor_system && (
                            <Tooltip title="Vendor System">
                              <CheckCircleIcon
                                sx={{
                                  width: 18,
                                  height: 18,
                                  color: "success.main",
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </StyledTableCell>
          
                      <StyledTableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                          <Typography variant="body2">
                            {formatAmount(spendingPowerUsed)}
                          </Typography>
                          {spendingPowerUsed > spendingPowerLimit && spendingPowerLimit > 0 && (
                            <Chip
                              label="Over Limit"
                              size="small"
                              color="error"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Stack>
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "#ff416c" }}
                        >
                          {formatAmount(spendingPowerLimit)}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700 }}
                          color="success.main"
                        >
                          {formatAmount(spendingPowerAvailable)}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Chip
                          label={company.account_status || "Unknown"}
                          size="small"
                          color={getStatusColor(company.account_status)}
                        />
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {isBlitzPay ? (
                          <Stack spacing={0.5} alignItems="center">
                            <Chip
                              icon={<FlashOnIcon />}
                              label="Active"
                              size="small"
                              sx={{
                                background:
                                  "linear-gradient(135deg, #ff416c 0%, #ff4b2b 90%)",
                                color: "white",
                              }}
                            />
                            {company.msa_initiated?.initiated && (
                              <Chip
                                label="MSA"
                                size="small"
                                variant="outlined"
                                color="primary"
                              />
                            )}
                            {company.starter_line_initiated?.initiated && (
                              <Chip
                                label="Starter"
                                size="small"
                                variant="outlined"
                                color="success"
                              />
                            )}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {hasCollabs ? (
                          <Chip
                            icon={<GroupWorkIcon />}
                            label="Enabled"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Tooltip
                          title={
                            copiedId === company.id
                              ? "Link copied!"
                              : "Copy onboarding link"
                          }
                        >
                          <IconButton
                            color={copiedId === company.id ? "success" : "primary"}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShareLink(company.name, company.id);
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </StyledTableCell>
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {paginationEnabled && (
            <TablePagination
              component="div"
              count={filteredCompaniesCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
            />
          )}
          </Paper>
        </>
      )}

      {/* BlitzPay Spending Power Dialog */}
      <Dialog
        open={spendingPowerDialogOpen}
        onClose={() => setSpendingPowerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FlashOnIcon sx={{ color: "#ff416c" }} />
              <Typography variant="h6">
                BlitzPay Nominal Spending Power
              </Typography>
            </Box>
            <IconButton
              edge="end"
              onClick={() => setSpendingPowerDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {blitzpayStats.companiesWithSpending.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="text.secondary">
                No companies with BlitzPay spending power configured
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                >
                  Total Nominal Spending Power
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: "#ff416c" }}
                >
                  {formatAmount(blitzpayStats.totalBlitzpaySpending)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  across {blitzpayStats.companiesWithSpending.length}{" "}
                  {blitzpayStats.companiesWithSpending.length === 1
                    ? "company"
                    : "companies"}
                </Typography>
              </Box>
              <List>
                {blitzpayStats.companiesWithSpending.map((company) => (
                  <ListItem key={company.id} disablePadding>
                    <ListItemButton
                      onClick={() =>
                        handleOpenCompanyFromDialog(company, () =>
                          setSpendingPowerDialogOpen(false)
                        )
                      }
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: "rgba(255, 65, 108, 0.08)",
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          src={company.phphref || ""}
                          sx={{ bgcolor: "#ff416c" }}
                        >
                          {company.name?.[0]?.toUpperCase() || "C"}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 600 }}
                            >
                              {company.name}
                            </Typography>
                            <FlashOnIcon
                              sx={{ width: 16, height: 16, color: "#ff416c" }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                              mt: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "#ff416c" }}
                            >
                              {formatAmount(
                                company.spending_power_limit
                              )}
                            </Typography>
                            {company.sub_account && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Sub-Account: {company.sub_account}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* MSA Initiated Dialog */}
      <Dialog
        open={msaDialogOpen}
        onClose={() => setMsaDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DescriptionIcon color="primary" />
              <Typography variant="h6">MSA Initiated Companies</Typography>
            </Box>
            <IconButton
              edge="end"
              onClick={() => setMsaDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {blitzpayStats.companiesWithMSA.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="text.secondary">
                No companies with MSA initiated
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2, p: 2, bgcolor: "primary.50", borderRadius: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                >
                  Total MSAs Initiated
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700 }}
                  color="primary"
                >
                  {blitzpayStats.companiesWithMSA.length}
                </Typography>
              </Box>
              <List>
                {blitzpayStats.companiesWithMSA.map((company) => (
                  <ListItem key={company.id} disablePadding>
                    <ListItemButton
                      onClick={() =>
                        handleOpenCompanyFromDialog(company, () =>
                          setMsaDialogOpen(false)
                        )
                      }
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: "primary.50",
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          src={company.phphref || ""}
                          sx={{ bgcolor: "#667eea" }}
                        >
                          {company.name?.[0]?.toUpperCase() || "C"}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 600 }}
                            >
                              {company.name}
                            </Typography>
                            <DescriptionIcon
                              sx={{ width: 16, height: 16 }}
                              color="primary"
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              Initiated by:{" "}
                              {company.msa_initiated?.initiated_by || "Unknown"}
                            </Typography>
                            {company.sub_account && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Sub-Account: {company.sub_account}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Starter Line Dialog */}
      <Dialog
        open={starterLineDialogOpen}
        onClose={() => setStarterLineDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TrendingFlatIcon color="success" />
              <Typography variant="h6">Starter Lines Active</Typography>
            </Box>
            <IconButton
              edge="end"
              onClick={() => setStarterLineDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {blitzpayStats.companiesWithStarterLine.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="text.secondary">
                No companies with starter line active
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2, p: 2, bgcolor: "success.50", borderRadius: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                >
                  Total Starter Lines Active
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700 }}
                  color="success.main"
                >
                  {blitzpayStats.companiesWithStarterLine.length}
                </Typography>
              </Box>
              <List>
                {blitzpayStats.companiesWithStarterLine.map((company) => (
                  <ListItem key={company.id} disablePadding>
                    <ListItemButton
                      onClick={() =>
                        handleOpenCompanyFromDialog(company, () =>
                          setStarterLineDialogOpen(false)
                        )
                      }
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: "success.50",
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          src={company.phphref || ""}
                          sx={{ bgcolor: "#4caf50" }}
                        >
                          {company.name?.[0]?.toUpperCase() || "C"}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 600 }}
                            >
                              {company.name}
                            </Typography>
                            <TrendingFlatIcon
                              sx={{ width: 16, height: 16 }}
                              color="success"
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              Initiated by:{" "}
                              {company.starter_line_initiated?.initiated_by ||
                                "Unknown"}
                            </Typography>
                            {company.sub_account && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Sub-Account: {company.sub_account}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Company Dialogs */}
      <CompanyDialog
        open={isPayCreatorDialogOpen}
        info={
          selectedIds.length
            ? companiesArray.find((c) => c.id === selectedIds[0])
            : undefined
        }
        onClose={(refresh) => {
          setPayCreatorDialogOpen(false);
          if (refresh) {
            queryClient.invalidateQueries(["companies"]);
          }
        }}
      />

      <CompanyDialog
        open={dialogOpen}
        info={selectedCompany}
        onClose={(refresh) => {
          setDialogOpen(false);
          setSelectedCompany(null);
          if (refresh) {
            queryClient.invalidateQueries(["companies"]);
          }
        }}
      />
    </Box>
  );
};

export default CompanyDetailsView;